// Watches a Mux/HTML5 media element for playback freezes - where the player is
// meant to be playing but currentTime stops advancing - and reports them once
// per freeze episode via the supplied callback.
//
// A freeze is a "waiting"/"stalled" state that persists for `thresholdMs` after
// the video has started playing, while the player is not paused, seeking or
// ended. That distinguishes a genuine freeze from ordinary start-up buffering,
// a deliberate pause, or a seek - the symptom reported on Microsoft Edge where
// the first few seconds play and then playback hangs.

const DEFAULT_THRESHOLD_MS = 7000;

export function createVideoStallWatchdog(player, onStall, options = {}) {
    if (!player || typeof onStall !== "function") {
        return () => {};
    }

    const thresholdMs = options.thresholdMs ?? DEFAULT_THRESHOLD_MS;
    const now = options.now ?? (() => Date.now());

    let hasPlayed = false;
    let lastTime = 0;
    let lastAdvanceAt = now();
    let stalledSince = null;
    let stallTimer = null;
    let hasReportedStall = false;

    function mediaEl() {
        return player.media?.nativeEl ?? player;
    }

    function clearStallTimer() {
        if (stallTimer !== null) {
            clearTimeout(stallTimer);
            stallTimer = null;
        }
    }

    function isIntentionallyStopped() {
        const el = mediaEl();
        return el.paused || el.ended || el.seeking;
    }

    function bufferedAheadSeconds() {
        try {
            const el = mediaEl();
            const buffered = el.buffered;
            const t = el.currentTime ?? 0;
            for (let i = 0; i < buffered.length; i++) {
                if (t >= buffered.start(i) && t <= buffered.end(i)) {
                    return Math.max(0, buffered.end(i) - t);
                }
            }
        } catch (e) {
            // buffered ranges can throw if the media element isn't ready yet
        }
        return 0;
    }

    function networkInfo() {
        // NetworkInformation API: Chromium + recent Safari expose this on
        // navigator.connection; Firefox doesn't. effectiveType bucketises the
        // connection (slow-2g/2g/3g/4g), downlink/rtt give numeric estimates,
        // saveData reflects the user's data-saver preference. Missing fields
        // stay null so the Bugsnag metadata reads cleanly.
        const conn = typeof navigator !== "undefined"
            ? (navigator.connection ?? navigator.mozConnection ?? navigator.webkitConnection)
            : null;
        return {
            online: typeof navigator !== "undefined" ? navigator.onLine : null,
            effectiveType: conn?.effectiveType ?? null,
            downlinkMbps: conn?.downlink ?? null,
            rttMs: conn?.rtt ?? null,
            saveData: conn?.saveData ?? null,
        };
    }

    function snapshot(extra = {}) {
        const el = mediaEl();
        return {
            playbackId: player.getAttribute?.("playback-id") ?? null,
            videoTitle: player.getAttribute?.("metadata-video-title") ?? null,
            currentTime: Number((el.currentTime ?? 0).toFixed(2)),
            duration: Number.isFinite(el.duration) ? Number(el.duration.toFixed(2)) : null,
            readyState: el.readyState ?? null,
            networkState: el.networkState ?? null,
            bufferedAheadSeconds: Number(bufferedAheadSeconds().toFixed(2)),
            network: networkInfo(),
            ...extra,
        };
    }

    function report(reason, extra = {}) {
        if (hasReportedStall) {
            return;
        }
        hasReportedStall = true;
        onStall({ reason, ...snapshot(extra) });
    }

    function beginStallWatch() {
        if (!hasPlayed || hasReportedStall || isIntentionallyStopped() || stallTimer !== null) {
            return;
        }
        stalledSince = now();
        stallTimer = setTimeout(() => {
            stallTimer = null;
            if (!isIntentionallyStopped() && now() - lastAdvanceAt >= thresholdMs) {
                report("stalled", { stalledForMs: now() - stalledSince });
            }
        }, thresholdMs);
    }

    function onTimeUpdate() {
        const t = mediaEl().currentTime ?? 0;
        if (t <= lastTime) {
            return;
        }
        lastTime = t;
        lastAdvanceAt = now();
        hasPlayed = true;
        clearStallTimer();
        if (hasReportedStall) {
            onStall({ reason: "recovered", ...snapshot() });
            hasReportedStall = false;
        }
    }

    function onPlaying() {
        hasPlayed = true;
        clearStallTimer();
    }

    function resolveMediaError(event) {
        // hls.js manifest failures (manifestLoadError, manifestIncompatibleCodecsError)
        // fire the error event *before* the native HTMLMediaElement.error is
        // populated, so reading mediaEl().error alone produces null events in
        // Bugsnag with no diagnostic info. Mux's MediaError (extends Error,
        // adds muxCode / errorCategory / context / fatal / streamType) ends
        // up on whichever of these is set first.
        return event?.detail ?? event?.error ?? mediaEl()?.error ?? player?.error ?? null;
    }

    function onError(event) {
        const err = resolveMediaError(event);
        report("error", {
            errorCode: err?.code ?? null,
            errorMessage: err?.message ?? null,
            // Mux MediaError extensions (see @mux/playback-core's errors.d.ts):
            // muxCode is the granular Mux error (e.g. 2404000 NETWORK_NOT_FOUND);
            // errorCategory is video|thumbnail|storyboard|drm; context is a
            // human-readable explanation Mux assembles for the dialog.
            muxCode: err?.muxCode ?? null,
            errorCategory: err?.errorCategory ?? null,
            errorContext: err?.context ?? null,
            errorFatal: err?.fatal ?? null,
            streamType: err?.streamType ?? null,
        });
    }

    const handlers = {
        timeupdate: onTimeUpdate,
        playing: onPlaying,
        waiting: beginStallWatch,
        stalled: beginStallWatch,
        pause: clearStallTimer,
        seeking: clearStallTimer,
        ended: clearStallTimer,
        error: onError,
    };

    Object.entries(handlers).forEach(([event, handler]) => {
        player.addEventListener(event, handler);
    });

    return function teardown() {
        clearStallTimer();
        Object.entries(handlers).forEach(([event, handler]) => {
            player.removeEventListener(event, handler);
        });
    };
}
